import type { PrincipalRole } from "@backchannel/tax-contracts";
import { useNavigate } from "@tanstack/react-router";
import { useId } from "react";
import { useI18n } from "~/i18n";
import { PREVIEWABLE_ROLES } from "~/lib/access/role-preview";
import { roleLabelKey } from "~/lib/access/roles";
import { useSession } from "~/lib/access/session-context";
import { NavIcon } from "./nav-icon";

/**
 * "Ver como" lives only in the account menu. It reads as a short single-choice
 * list: the operator's own role first, then each previewable role, with a
 * check on the one in effect.
 */
export function RolePreviewPicker({ onSelect }: { onSelect?: () => void }) {
  const { t } = useI18n();
  const { canPreviewRoles, previewRole, setPreviewRole } = useSession();
  const navigate = useNavigate();
  const labelId = useId();
  const helpId = useId();
  if (!canPreviewRoles) return null;

  const choose = (role: PrincipalRole | null) => {
    setPreviewRole(role);
    onSelect?.();
    if (role === "TAX_DEVELOPER") {
      void navigate({
        to: "/settings",
        search: { workspace: "developers" },
        hash: "integrations",
      });
    } else if (role === "TAX_COMMERCIAL") {
      void navigate({ to: "/commercial", search: {} });
    } else if (role) {
      void navigate({ to: "/overview", search: {} });
    }
  };

  const options: Array<{ role: PrincipalRole | null; label: string }> = [
    { role: null, label: t("shell.viewAs.self", { role: t(roleLabelKey("SUPER_ADMIN")) }) },
    ...PREVIEWABLE_ROLES.map((role) => ({ role, label: t(roleLabelKey(role)) })),
  ];

  return (
    <section className="role-preview" aria-labelledby={labelId} aria-describedby={helpId}>
      <strong className="role-preview__title" id={labelId}>
        {t("nav.viewAs.label")}
      </strong>
      <p className="role-preview__help" id={helpId}>
        {t("nav.viewAs.help")}
      </p>
      <div className="role-preview__options">
        {options.map((option) => {
          const selected = previewRole === option.role;
          return (
            <button
              key={option.role ?? "self"}
              type="button"
              className="role-preview__option"
              aria-pressed={selected}
              onClick={() => choose(option.role)}
            >
              <span className="role-preview__check" aria-hidden="true">
                {selected ? <NavIcon name="check" /> : null}
              </span>
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
